// Tech Dashboard: a Gantt of the development plan, and a non-technical bug and
// feature intake lives beside it in Platform Requests.
//
// Built 8 September 2026, against objects that already existed: see
// db/migrations/0174 for the provenance of dev_plan_item, dev_plan_dependency,
// v_dev_plan_gantt and v_dev_plan_phase_summary, all live since before this
// file. This page is the first screen to read them.
//
// Reads are gated on canView, same as every other page. Writes are gated on
// canEditDevPlan (or isAdmin), enforced here for the UI affordance AND again,
// independently, inside dev_plan_item_upsert_v1, dev_plan_item_set_status_v1
// and dev_plan_set_dependencies_v1 themselves: this handler's check is a fast,
// readable no, not the actual authority. Every write control below is hidden
// unless getDevPlan's own response says can_edit; nothing here infers that
// from a capability flag the frontend already holds, because the global
// capabilities context does not carry canEditDevPlan at all (checked directly:
// see hooks/useCapabilities.tsx). can_edit is computed server side and read
// off the response, the same pattern the Pipeline tab already uses.
//
// Every derived figure on this screen (variance, overdue, dependency state,
// blocks count, phase counts) is read from v_dev_plan_gantt or
// v_dev_plan_phase_summary exactly as the query returns it. Nothing here
// recomputes any of them.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { GanttChartSquare, Plus, ShieldAlert } from 'lucide-react'
import {
  useGetDevPlan,
  useGetDevPlanRefs,
  useSetDevPlanDependencies,
  useSetDevPlanStatus,
  useUpsertDevPlanItem,
} from '../hooks/backend/devplan'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { Badge } from '../lib/shadcn/badge'
import { Button } from '../lib/shadcn/button'
import { TooltipProvider } from '../lib/shadcn/tooltip'
import { toast } from '../lib/shadcn/sonner'
import { writeErrorMessage } from '../utils/writeError'
import { DevPlanFilters } from '../components/devplan/DevPlanFilters'
import { PhaseSummaryStrip } from '../components/devplan/PhaseSummaryStrip'
import { GanttChart } from '../components/devplan/GanttChart'
import { DevPlanTable } from '../components/devplan/DevPlanTable'
import { DevPlanEditPanel, type DevPlanEditTarget } from '../components/devplan/DevPlanEditPanel'
import { AskTheTeamDialog } from '../components/devplan/AskTheTeamDialog'
import {
  EMPTY_DEV_PLAN_FILTER,
  filterDevPlanItems,
  ownerOptionsFromItems,
  type DevPlanFilterState,
  type DevPlanRefsFeed,
  type GanttItem,
  type PhaseSummary,
  type SortColumn,
} from '../utils/devPlan'

type DevPlanFeed = {
  items: GanttItem[]
  phase_summary: PhaseSummary[]
  can_edit: boolean
}

export default function TechDashboard() {
  const { loading: accessLoading, canView, caption } = useCapabilities()

  const plan = useGetDevPlan<DevPlanFeed>()
  const refs = useGetDevPlanRefs<DevPlanRefsFeed>()
  const setStatus = useSetDevPlanStatus()
  const upsertItem = useUpsertDevPlanItem()
  const setDependencies = useSetDevPlanDependencies()

  const planTrigger = plan.trigger
  const refsTrigger = refs.trigger

  const loadPlan = useCallback(() => {
    return planTrigger({}, { skipCache: true })
  }, [planTrigger])

  const loadRefs = useCallback(() => {
    refsTrigger({}, { skipCache: true })
  }, [refsTrigger])

  useEffect(() => {
    if (canView) loadPlan()
  }, [canView, loadPlan])

  useEffect(() => {
    if (canView) loadRefs()
  }, [canView, loadRefs])

  const [filter, setFilter] = useState<DevPlanFilterState>(EMPTY_DEV_PLAN_FILTER)
  const [sortColumn, setSortColumn] = useState<SortColumn>('planned_end')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [selectedRef, setSelectedRef] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [busy, setBusy] = useState(false)

  const feed = plan.data
  const items = feed?.items ?? []
  const canEdit = feed?.can_edit === true

  const filteredItems = useMemo(() => filterDevPlanItems(items, filter), [items, filter])
  const ownerOptions = useMemo(() => ownerOptionsFromItems(items), [items])
  const phaseOptions = refs.data?.phases ?? []
  const statusOptions = refs.data?.statuses ?? []
  const assignees = refs.data?.assignees ?? []

  const selectedItem = selectedRef ? items.find((i) => i.ref === selectedRef) ?? null : null

  const editTarget: DevPlanEditTarget | null = creating
    ? { mode: 'create' }
    : selectedItem
      ? { mode: 'edit', item: selectedItem }
      : null

  const headline = useMemo(() => {
    const open = items.filter((i) => i.status !== 'done' && i.status !== 'superseded')
    return {
      open: open.length,
      overdue: open.filter((i) => i.is_overdue).length,
      waitingOnDecision: open.filter((i) => i.dependency_state === 'waiting_on_decision').length,
    }
  }, [items])

  function onSort(column: SortColumn) {
    if (column === sortColumn) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  function selectRow(ref: string) {
    setCreating(false)
    setSelectedRef((prev) => (prev === ref ? null : ref))
  }

  function startCreating() {
    setSelectedRef(null)
    setCreating(true)
  }

  function closeEditPanel() {
    setCreating(false)
    setSelectedRef(null)
  }

  async function handleSaveDetails(p: {
    ref: string
    phase_key: string
    title: string
    planned_start: string | null
    planned_end: string | null
    owner_person_id: string | null
  }) {
    setBusy(true)
    try {
      await upsertItem.trigger(p, { skipCache: true })
      toast.success(creating ? 'Plan item created.' : 'Plan item updated.')
      setCreating(false)
      await loadPlan()
      setSelectedRef(p.ref)
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function handleSaveStatus(p: {
    ref: string
    status: string
    actual_completion: string | null
    reason: string | null
  }) {
    setBusy(true)
    try {
      await setStatus.trigger(p, { skipCache: true })
      toast.success('Plan item updated.')
      loadPlan()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function handleSaveDependencies(p: { ref: string; depends_on_refs: string[] }) {
    setBusy(true)
    try {
      await setDependencies.trigger(p, { skipCache: true })
      toast.success('Dependencies updated.')
      loadPlan()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            The development plan is available to signed-in Fleetly staff.
          </p>
        </div>
      </div>
    )
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex h-full min-h-0 flex-col overflow-y-auto bg-card px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <GanttChartSquare className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-base font-semibold">Tech Dashboard</h1>
            <p className="truncate text-xs text-muted-foreground">{caption}</p>
          </div>
          {canEdit && (
            <Button type="button" size="sm" onClick={startCreating} className="gap-1">
              <Plus className="h-3.5 w-3.5" /> New item
            </Button>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <Badge variant="outline">{headline.open} open</Badge>
          {headline.overdue > 0 && <Badge variant="destructive">{headline.overdue} past their date</Badge>}
          {headline.waitingOnDecision > 0 && (
            <Badge variant="warning">{headline.waitingOnDecision} waiting on a decision</Badge>
          )}
        </div>

        <div className="mt-4">
          {plan.loading && !feed && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner className="h-3.5 w-3.5" /> Loading the development plan…
            </div>
          )}
          {plan.error && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
              {plan.error}
            </p>
          )}
        </div>

        {feed && (
          <div className="mt-3 space-y-5">
            <section>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Phase summary
              </h2>
              <PhaseSummaryStrip phases={feed.phase_summary} />
            </section>

            <section>
              <DevPlanFilters
                value={filter}
                onChange={setFilter}
                phases={phaseOptions}
                statuses={statusOptions}
                owners={ownerOptions}
              />
            </section>

            <section>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Gantt
              </h2>
              <GanttChart items={filteredItems} />
            </section>

            <section>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Detail
              </h2>
              <DevPlanTable
                items={filteredItems}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={onSort}
                onSelect={selectRow}
                selectedRef={selectedRef}
              />
            </section>

            {/* A question that is holding an item up belongs on the board with a
                name and a date on it, not in a note. Shown only on a selected
                item, because a question about nothing in particular is the kind
                nobody answers. */}
            {selectedItem && canEdit && (
              <section>
                <AskTheTeamDialog
                  itemRef={selectedItem.ref}
                  itemTitle={selectedItem.title}
                  onRaised={() => void loadPlan()}
                />
              </section>
            )}

            {editTarget && canEdit && (
              <DevPlanEditPanel
                target={editTarget}
                phases={phaseOptions}
                statuses={statusOptions}
                assignees={assignees}
                allItems={items}
                busy={busy}
                onSaveDetails={handleSaveDetails}
                onSaveStatus={handleSaveStatus}
                onSaveDependencies={handleSaveDependencies}
                onClose={closeEditPanel}
              />
            )}
          </div>
        )}
      </div>
    </TooltipProvider>
  )
}
